import React, { useState, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components/macro';
import queryString from 'query-string';
import { parseISO, format } from 'date-fns';

import { TablePagination } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import DeleteIcon from '@material-ui/icons/Delete';
import Tooltip from '@material-ui/core/Tooltip';
import Checkbox from '@material-ui/core/Checkbox';
import {
  showToast,
  Tables,
  Modal,
  Button,
  ModalTitle,
  ModalActions,
} from '@deeptrue-frontend/ui';

import api from 'api';
import { OrganizationUsersType } from 'store/modules/organizations/organizations';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import { useFormatMessage } from 'components/IntlProvider';
import { parseDataTable } from 'utils';
import auth from 'consts/auth';

const { withRouter, Link, ROUTES } = RouterProvider;
const { TableRow, TableCell, TableBody, TableHeader } = Tables;

const Table = styled(Tables.Table)`
  margin-top: 24px;

  .icons-wrapper {
    svg {
      cursor: pointer;

      &:first-child {
        margin-right: 8px;
      }
    }
  }
`;

interface UsersProps extends Pick<OrganizationUsersType, 'count' | 'list'> {
  currentPage: number;
  isAdmin: boolean;
  refresh: () => void;
}

const Users = ({
  count,
  list,
  history,
  currentPage,
  location: { pathname },
  refresh,
  isAdmin,
}: UsersProps & RouteComponentProps) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [userToRemove, setUserToRemove] = useState({
    uuid: '',
    organization_name: '',
    email: '',
  });
  const t = useFormatMessage();
  const onPageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number
  ) => {
    const params = { limit: 20, offset: 20 * page };
    history.push(`${pathname}?${queryString.stringify(params)}`);
  };

  const deleteUserFromOrganization = (
    uuid: string,
    organization_name: string
  ) =>
    api.resources
      .removeOrganizationUser({ uuid, organization_name })
      .then(() => {
        refresh();
        setIsModalVisible(false);
      });

  const demoteUser = (uuid: string, organization_name: string) =>
    api.resources
      .demoteOrganizationUser({ uuid, organization_name })
      .then(() => {
        refresh();
      })
      .catch(({ response }) => {
        showToast({
          type: 'error',
          title: response.data.detail,
        });
      });

  const promoteUser = (uuid: string, organization_name: string) =>
    api.resources
      .promoteOrganizationUser({ uuid, organization_name })
      .then(() => {
        refresh();
      });

  const handleRoleChange =
    (uuid: string, organization_name: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        return promoteUser(uuid, organization_name);
      }
      return demoteUser(uuid, organization_name);
    };

  const headerCells = useMemo(
    () => [
      t('dashboard.organizations.users.email', 'Email'),
      t('dashboard.organizations.users.username', 'Username'),
      t('dashboard.organizations.users.firstname', 'First name'),
      t('dashboard.organizations.users.lastname', 'Last name'),
      t('dashboard.organizations.users.role', 'Admin'),
      t('dashboard.organizations.users.createdat', 'Created date'),
      t('dashboard.organizations.users.actions', 'Actions'),
    ],
    // eslint-disable-next-line
    []
  );

  return (
    <>
      <Table>
        <TableHeader cells={headerCells} />

        <TableBody>
          {list.map((user) => (
            <TableRow key={user.uuid}>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.username}</TableCell>
              <TableCell>{parseDataTable(user.first_name)}</TableCell>
              <TableCell>{parseDataTable(user.last_name)}</TableCell>
              <TableCell>
                {isAdmin ? (
                  <Checkbox
                    checked={
                      user.organizations[0].organization_role ===
                      auth.ORGANIZATION_ROLE.ADMIN
                    }
                    onChange={handleRoleChange(
                      user.uuid,
                      user.organizations[0].codename
                    )}
                    value="admin"
                    color="primary"
                  />
                ) : (
                  t(
                    `user.role.${user.organizations[0].organization_role}`,
                    ['Admin', 'Member'][
                      user.organizations[0].organization_role - 1
                    ]
                  )
                )}
              </TableCell>
              <TableCell>
                {format(parseISO(user.created_at), 'dd-MM-yyyy')}
              </TableCell>
              <TableCell className="icons-wrapper">
                <Link
                  to={`${ROUTES.ADMIN.ORGANIZATIONS.LIST}/${user.organizations[0].codename}/users/${user.uuid}`}
                >
                  <Tooltip
                    title={t('tooltip.users.info', 'User Info')}
                    placement="top"
                  >
                    <InfoIcon />
                  </Tooltip>
                </Link>
                <span
                  onClick={() => {
                    setIsModalVisible(true);
                    setUserToRemove({
                      email: user.email,
                      uuid: user.uuid,
                      organization_name: user.organizations[0].codename,
                    });
                  }}
                >
                  <Tooltip
                    title={t('tooltip.users.delete', 'Delete user')}
                    placement="top"
                  >
                    <DeleteIcon />
                  </Tooltip>
                </span>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TablePagination
              count={count}
              page={currentPage}
              rowsPerPage={20}
              onPageChange={onPageChange}
              rowsPerPageOptions={[20]}
            />
          </TableRow>
        </TableBody>
      </Table>
      <Modal
        isVisible={isModalVisible}
        onBackdropClick={() => setIsModalVisible(false)}
        onCloseClick={() => setIsModalVisible(false)}
      >
        <ModalTitle>
          {t(
            'dashboard.organizations.users.remove',
            'Are you sure you want to delete { email } user',
            {
              email: userToRemove.email,
            }
          )}
        </ModalTitle>
        <ModalActions>
          <Button
            variant="primary"
            style={{ marginRight: 12 }}
            onClick={() =>
              deleteUserFromOrganization(
                userToRemove.uuid,
                userToRemove.organization_name
              )
            }
          >
            {t('common.yes', 'Yes')}
          </Button>
          <Button variant="secondary" onClick={() => setIsModalVisible(false)}>
            {t('common.no', 'No')}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default withRouter(Users);
